import { DatePipe } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { NavigationStart, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ProblemReportRequest, ProblemReportScreenshot, ProblemReportService } from '../../../@core/services/problem-report.service';
import { ProblemReportContext, ProblemReportDraftService } from '../../../@core/services/problem-report-draft.service';
import { TokenJwtService } from '../../../@core/services/tokenJwt.service';
import { SectorService } from '../../../@core/services/sector.service';
import { StopService } from '../../../@core/services/stop.service';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ProblemReportLoginDialogComponent } from './problem-report-login-dialog.component';

const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024;
const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

@Component({
  selector: 'ngx-problem-report-dialog',
  templateUrl: './problem-report-dialog.component.html',
  styleUrls: ['./problem-report-dialog.component.scss']
})
export class ProblemReportDialogComponent implements OnInit, OnDestroy {
  readonly form = new FormGroup({
    description: new FormControl('', [Validators.required, Validators.maxLength(4000)])
  });
  screenshot: ProblemReportScreenshot;
  previewUrl: string;
  fileError: string;
  submitError: string;
  ticketId: string | number;
  ticketUrl: string;
  submitting = false;
  readingFile = false;
  submittedFingerprint: string;
  private restoredContext: ProblemReportContext;
  private navigationSubscription: Subscription;
  private stopInvalidTokenHandler: () => void;
  private loginDialogRef: MatDialogRef<ProblemReportLoginDialogComponent>;
  private destroyed = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ProblemReportDialogComponent>,
    private reportService: ProblemReportService,
    private sectorService: SectorService,
    private stopService: StopService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private draftService: ProblemReportDraftService,
    private tokenJwtService: TokenJwtService,
    private router: Router,
    private dialog: MatDialog,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.navigationSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && event.url.split('?')[0] === '/login') {
        this.dialogRef.close();
      }
    });
    this.stopInvalidTokenHandler = this.tokenJwtService.onInvalidToken(() => this.requestLogin());
    const draft = this.draftService.get();
    if (draft) {
      this.description.setValue(draft.description);
      this.screenshot = draft.screenshot;
      this.restoredContext = draft.context;
      if (this.screenshot) {
        this.previewUrl = 'data:' + this.screenshot.contentType + ';base64,' + this.screenshot.contentBase64;
      }
    }
    if (this.tokenJwtService.isTokenExpired()) {
      this.tokenJwtService.renewToken().pipe(takeUntil(this.destroyed)).subscribe(
        () => {},
        error => { if (error && error.status === 401) { this.requestLogin(); } }
      );
    }
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) { this.navigationSubscription.unsubscribe(); }
    if (this.stopInvalidTokenHandler) { this.stopInvalidTokenHandler(); }
    if (this.loginDialogRef) { this.loginDialogRef.close(false); }
    this.destroyed.next();
    this.destroyed.complete();
  }

  get description(): FormControl { return this.form.get('description') as FormControl; }

  selectFile(event: Event): void {
    if (this.submitting || this.readingFile) { return; }
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      this.readFile(input.files[0]);
    }
    input.value = '';
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (this.submitting || this.readingFile) { return; }
    const items = event.clipboardData && event.clipboardData.items;
    if (!items) { return; }
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          event.preventDefault();
          this.readFile(file);
          return;
        }
      }
    }
  }

  removeScreenshot(): void {
    if (this.submitting || this.readingFile) { return; }
    this.screenshot = undefined;
    this.previewUrl = undefined;
    this.fileError = undefined;
    this.descriptionChanged();
  }

  descriptionChanged(): void {
    if (this.ticketId !== undefined) {
      this.ticketId = undefined;
      this.ticketUrl = undefined;
    }
    this.saveDraft();
  }

  submit(): void {
    this.submitError = undefined;
    this.form.markAsTouched();
    if (this.form.invalid || this.submitting || this.readingFile) { return; }
    if (!this.description.value || !this.description.value.trim()) {
      this.description.setErrors({ required: true });
      return;
    }

    const request = this.createRequest();
    const fingerprint = JSON.stringify(request);
    if (fingerprint === this.submittedFingerprint) { return; }

    this.submitting = true;
    this.tokenJwtService.renewToken().pipe(takeUntil(this.destroyed)).subscribe(
      () => this.submitReport(request, fingerprint),
      error => this.handleSendError(error)
    );
  }

  private submitReport(request: ProblemReportRequest, fingerprint: string): void {
    this.reportService.submit(request).pipe(takeUntil(this.destroyed)).subscribe(
      response => {
        this.submitting = false;
        if (!response || response.freshdeskTicketId === undefined || response.freshdeskTicketId === null || response.freshdeskTicketId === '') {
          this.submitError = this.translate.instant('problem-report.submit-error');
          return;
        }
        this.ticketId = response.freshdeskTicketId;
        this.ticketUrl = response.freshdeskTicketUrl;
        this.submittedFingerprint = fingerprint;
        this.draftService.clear();
      },
      (error: HttpErrorResponse) => {
        this.handleSendError(error);
      }
    );
  }

  private handleSendError(error: HttpErrorResponse): void {
    this.submitting = false;
    if (error && error.status === 401) {
      this.requestLogin();
    } else {
      this.submitError = this.translate.instant('problem-report.submit-error');
    }
  }

  close(): void {
    if (!this.submitting && !this.readingFile) {
      this.draftService.clear();
      this.dialogRef.close();
    }
  }

  private readFile(file: File): void {
    this.fileError = undefined;
    if (SCREENSHOT_TYPES.indexOf(file.type) < 0) {
      this.fileError = this.translate.instant('problem-report.invalid-image-type');
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      this.fileError = this.translate.instant('problem-report.image-too-large');
      return;
    }
    if (file.size === 0) {
      this.fileError = this.translate.instant('problem-report.invalid-image');
      return;
    }

    const reader = new FileReader();
    this.readingFile = true;
    reader.onload = () => {
      this.readingFile = false;
      const result = String(reader.result);
      const comma = result.indexOf(',');
      this.screenshot = {
        fileName: file.name || 'screenshot',
        contentType: file.type,
        sizeBytes: file.size,
        contentBase64: comma >= 0 ? result.substring(comma + 1) : result
      };
      this.previewUrl = result;
      this.submittedFingerprint = undefined;
      this.descriptionChanged();
    };
    reader.onerror = () => {
      this.readingFile = false;
      this.fileError = this.translate.instant('problem-report.invalid-image');
    };
    reader.readAsDataURL(file);
  }

  private createRequest(): ProblemReportRequest {
    const context = this.reportContext();
    return {
      description: this.description.value.trim(),
      currentUrl: context.currentUrl,
      browserInfo: navigator.userAgent,
      screenResolution: window.screen ? window.screen.width + 'x' + window.screen.height : '',
      applicationVersion: environment.appVersion,
      sectorId: context.sectorId,
      routeId: context.routeId,
      routeDate: context.routeDate,
      ...(this.screenshot ? { screenshot: this.screenshot } : {})
    } as ProblemReportRequest;
  }

  private saveDraft(): void {
    if (this.ticketId === undefined) {
      this.draftService.save(this.description.value || '', this.screenshot, this.reportContext());
    }
  }

  private reportContext(): ProblemReportContext {
    if (this.restoredContext) { return this.restoredContext; }
    const routeDate = this.datePipe.transform(this.sectorService.RouteDate || this.stopService.RouteDate, 'yyyy-MM-dd');
    return {
      currentUrl: window.location.origin + window.location.pathname,
      sectorId: this.numberOrNull(this.sectorService.SectorID),
      routeId: this.numberOrNull(this.stopService.RouteID),
      routeDate: routeDate || null
    };
  }

  private requestLogin(): void {
    if (this.ticketId !== undefined) { return; }
    this.saveDraft();
    this.submitError = this.translate.instant('problem-report.session-expired');
    if (this.loginDialogRef) { return; }
    const userName = this.authenticationService.currentUserValue && this.authenticationService.currentUserValue.UserName;
    this.loginDialogRef = this.dialog.open(ProblemReportLoginDialogComponent, {
      width: '420px', maxWidth: '95vw', disableClose: true, data: { userName }
    });
    this.loginDialogRef.afterClosed().subscribe(authenticated => {
      this.loginDialogRef = undefined;
      if (authenticated) { this.submitError = undefined; }
    });
  }

  private numberOrNull(value: number): number {
    return value && value > 0 ? value : null;
  }
}
